/**
 * UTM / marketing parameter preservation for redirects to my.brain-event.com
 */

const STORED_QUERY_KEY = 'brain_landing_marketing_query_v1';
const MAX_STORED_LEN = 2048;

function loadStoredSearchParams() {
  try {
    const raw = sessionStorage.getItem(STORED_QUERY_KEY);
    if (!raw) return new URLSearchParams();
    return new URLSearchParams(raw);
  } catch {
    return new URLSearchParams();
  }
}

function saveStoredSearchParams(params) {
  const qs = params.toString();
  if (!qs) return;
  const toStore = qs.length > MAX_STORED_LEN ? qs.slice(0, MAX_STORED_LEN) : qs;
  try {
    sessionStorage.setItem(STORED_QUERY_KEY, toStore);
  } catch {
    /* private mode / quota */
  }
}

/** Merge incoming into base; incoming wins on key conflict. */
function mergeSearchParams(base, incoming) {
  const out = new URLSearchParams(base);
  incoming.forEach((value, key) => {
    out.set(key, value);
  });
  return out;
}

/**
 * Read current URL params and persist (session) so SPA routes like /quiz keep attribution.
 */
export function captureMarketingParamsFromUrl() {
  const fromUrl = new URLSearchParams(window.location.search);
  if ([...fromUrl.keys()].length === 0) return loadStoredSearchParams();

  const merged = mergeSearchParams(loadStoredSearchParams(), fromUrl);
  saveStoredSearchParams(merged);
  return merged;
}

/**
 * All marketing params: stored (first landing) ∪ current URL.
 */
export function getAllUtmParams() {
  const merged = mergeSearchParams(loadStoredSearchParams(), new URLSearchParams(window.location.search));
  if ([...new URLSearchParams(window.location.search).keys()].length > 0) {
    saveStoredSearchParams(merged);
  }
  const params = {};
  merged.forEach((value, key) => {
    if (value) params[key] = value;
  });
  return params;
}

/**
 * Build query string from parameters object
 */
export function buildQueryString(params) {
  const entries = Object.entries(params);
  if (entries.length === 0) return '';
  const queryString = entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return `?${queryString}`;
}

/**
 * Append marketing params to destination URL without overwriting existing destination keys
 * (e.g. keep direct_seat_selector & ticket_id on offer deep links).
 */
export function appendUtmParams(baseUrl) {
  captureMarketingParamsFromUrl();
  const marketing = getAllUtmParams();
  if (Object.keys(marketing).length === 0) return baseUrl;

  let dest;
  try {
    dest = new URL(baseUrl);
  } catch {
    return baseUrl;
  }

  for (const [key, value] of Object.entries(marketing)) {
    if (!dest.searchParams.has(key)) {
      dest.searchParams.set(key, value);
    }
  }

  return dest.toString();
}

export function getTicketPurchaseUrl() {
  return appendUtmParams('https://my.brain-event.com');
}

export function initUtmParamTracking() {
  captureMarketingParamsFromUrl();
}
